/**
 * NGX Application Core
 * Backbone Definitions, Routing, Templates, Events
 *
 * NGX.App.init();
 */

/* global $, _, FB, NGX, Insights, Backbone, Handlebars */

import sharingNetworks from '../../../console/common/enums/sharing-networks.js';
import enums from '../../common/config/enums';
import { AnalyticsEvents as PUB } from '../../common/config/api.js';
import { getParentParams, isSupportedBrowser, applyUAClasses } from '../utils/browser';
import { mountVideoPlayer } from '../utils/domUtils';
import { facebookShare } from './Share';

/**
 * Wrap testing code that we want to gate in a conditional using ngxGatedCode
 *
 * @example
 *
 *  if (ngxGatedCode(NGX.App.state.pageInfo.url) === 'TheEmpire') {
 *    ...
 *  }
 *
 *  if (ngxGatedCode(pageInfo.url)) {
 *    ...
 *  }
 *
 *  @param {string} the full url (window.location.href) of the parent window, if not in a iframe then it'll use the
 *                  current window.location
 *  @return {string|boolean} the value of ?ngxdebug=<string> or true if 'ngxdebug' in query string.
 */
function ngxGatedCode(parentUrl) {
  if (window.location === window.top.location) {
    // no-iframe, use our current location
    parentUrl = window.location.href;
  }
  if (!(parentUrl && typeof parentUrl === 'string' && parentUrl.indexOf('?') !== -1)) {
    return false;
  }
  var search = parentUrl
    .slice(parentUrl.indexOf('?') + 1)
    .split('&')
    .reduce(function(obj, kv) {
      var params = kv.split('=');
      obj[params[0]] = decodeURIComponent(params[1]);
      return obj;
    }, {});

  return 'ngxdebug' in search ? search.ngxdebug || true : false;
}

/** App */
export default (function(w, d, $, _, undefined) {
  'use strict';

  var Components = false,
    App = false,
    Router = false,
    IntegrationEvents = {},
    Events = {}, // Backbone.Events
    Templates = {}, // Handlebars templates
    // App flow objects
    Actions = false,
    States = false,
    // Component View override definitions
    viewDef = {},
    // trackQueue contains component view events
    trackQueue = [],
    // Application state and configuration JSON
    Config = {},
    State = {
      loadedSDKs: [],
    },
    /**
     * page config
     */

    conf = {
      // application root view
      // TODO: display object ID is this el's id. use that once we've tidied up a bit.
      app:       '.xPage',
      // selector for pages within the app
      section:   '.xSectionContent',
      // selector for individual components
      component: '.xComponent',
    },
    /**
     * Remove form content and replace with a gate
     * @param {Object} cfg
     */
    gateApp = function(cfg) {
      var _el = cfg && cfg.hasOwnProperty('el') ? cfg.el : '.xPageInner',
        _val = true,
        _type = cfg && cfg.hasOwnProperty('type') ? cfg.type : '';

      $('body').addClass('x' + _type + 'Gated');
      $(_el).html(NGX.lang.get('form.coppa.title'));

      // Show modal gate warning
      NGX.overlay.create({
        template: NGX.App.api.getTemplate('inviteModal'),
        type:     'modal x' + _type + 'Gate',
        content:  {
          title: NGX.lang.get('form.coppa.title'),
          msg:   NGX.lang.get('form.coppa.msg'),
        },
        callback: function() {
          if (cfg && cfg.hasOwnProperty('store') && cfg.store === true) {
            if (cfg.data) {
              _val = NGX.Util.hashCode(cfg.data);
            }
            // Privacy management note: coppa cookie usage is "strictly necessary" and doesn't need to be privacy-gated.
            // If cookies are browser-disabled, we display a blocking cookies-disabled modal and the experience & won't get here.
            NGX.StorageManager.push('gate_' + NGX.App.config.tenant, _val, 'local');
          }
        },
      });
    },
    /**
     * Check users eligibility to view this campaign
     */
    canSeeCampaign = function() {
      // Is this a remember me campaign?
      const rememberMeEnabled = document.getElementById('xReturningUserForm') ? true : false;

      // If not a remember me campaign.
      if (!rememberMeEnabled) {
        // Privacy management note: coppa cookie usage is "strictly necessary" and doesn't need to be privacy-gated.
        // If cookies are browser-disabled, we display a blocking cookies-disabled modal and the experience & won't get here.
        const _blocked = NGX.StorageManager.get('gate_' + NGX.App.config.tenant);
        const _hashed = NGX.Util.hashCode(NGX.App.config.id);

        if (_blocked.indexOf(_hashed) > -1) {
          // Gate the campaign
          NGX.App.api.gate({ type: 'Age' });
        }
      }
    },
    /**
     * Send all pending tracking events to Insights engine
     */
    processTrackQueue = function() {
      var i = 0,
        len = trackQueue.length;
      // execute pushed event immediately to prevent more events being added to queue
      trackQueue.push = function(evt) {
        NGX.Insights.track(evt);
      };
      // process existing queue
      for (; i < len; i++) {
        NGX.Insights.track(trackQueue[i]);
      }
    },
    /**
     * Refresh all (or individual) components
     * @oaram {string} id
     */

    refreshComponents = function(id) {
      log.debug('Refreshing components: [' + (id || 'all') + ']', 3);
      var _cmps = id && Components.get(id) ? [Components.get(id)] : Components.toArray();

      _.each(
        _cmps,
        function(el, idx) {
          var _view = el.get('view');
          if (_view && 'function' === typeof _view.reload) {
            _view.reload.call(this);
          }
        }.bind(this)
      );
    },
    // extend execution context with NGX.objName then delete to cleanup global
    mergeAndPurge = function(obj) {
      if (this !== w && _.isObject(NGX[obj])) {
        _.extend(this, NGX[obj]);
        delete NGX[obj];
      }
    },
    // something is ready - update App State and trigger Event
    stateReady = function(name) {
      State[name + 'Ready'] = true;
      Events.trigger(name + ':ready');
      log.debug(name + ' ready', 2);

      if (name === 'app') {
        $(window).trigger('app:ready');
        window.dispatchEvent(new CustomEvent('app:ready'));
      }
    },
    // get the current query sting, or supply values to extend the current qs object
    updateQueryString = function(options) {
      var qs = decodeURIComponent(w.location.search),
        qsObj = {},
        qsArr,
        len,
        item,
        i = 0;
      if (_.isObject(options)) {
        _.extend(State.query, options);
      } else if (qs) {
        qsArr = qs.substring(1).split('&');
        len = qsArr.length;
        for (; i < len; i++) {
          item = qsArr[i].split('=');
          qsObj[item[0]] = item.length > 1 ? item[1] : undefined;
        }
        State.query = qsObj;
      }
      return State.query;
    },
    // get querystring from the State object
    getQuerystring = function() {
      if (State.query) {
        return '?' + $.param(State.query);
        //return '?' + decodeURIComponent($.param(State.query));
      }
      return '';
    },
    // register a new view type
    registerView = function(type, view) {
      viewDef[type] = view;
    },
    // register all views
    registerViews = function() {
      if (NGX.comps) {
        do {
          const comp = NGX.comps.pop();
          registerView(comp.type, comp.view);
        } while (NGX.comps.length > 0);
        delete NGX.comps;
      }
    },
    // return a Handlebars template
    getTemplate = function(id) {
      if ('function' === typeof id) return id;
      try {
        if ('function' === typeof NGX.templates[id]) {
          return NGX.templates[id];
        }
      } catch (error) {
        log.debug(error, 1);
        return Templates[id] || false;
      }
    },
    // create a new Handlebars template from a string
    addTemplate = function(el, val) {
      return el && val && 'function' === typeof Handlebars.compile ? (Templates[el] = Handlebars.compile(val)) : false;
    },
    // return an object containing the current campaign form
    getForm = function(formID, formElem) {
      if (formElem) {
        return {
          el:   formElem,
          type: 'inline'
        };
      }
      var el = d.getElementById(formID || 'xCampaignForm'),
        type = el ? 'inline' : 'hidden',
        form = el || d.getElementById('xHiddenForm');
      return {
        el:   form,
        type: form ? type : undefined,
      };
    },
    // get multi-variate a/b test ID
    getTestID = function() {
      return NGX.App.config.testID || undefined;
    },
    // set multi-variate a/b test ID
    setTestID = function(testID) {
      NGX.App.config.testID = testID;
      setFormField('ngx_test_id', testID, null, null, null, true);
      return testID;
    },
    // get external customer ID for entries
    getCustomerID = function() {
      return NGX.App.config.externalCustomerID || undefined;
    },
    // set external customer ID for entries.  this will get added to the entry when it is submitted to the BE
    setCustomerID = function(externalCustomerID, externalCustomerIDType) {
      // To prevent errors due to numeric ids beyond the precision range, when customers are using massively long loyalty ids.
      if (typeof externalCustomerID !== 'string') {
        log.debug('Error: setCustomerID only accepts string values, please convert numeric IDs to string.');
        return;
      }

      // Strip out special characters, only accept upper and lowercase letters, numbers, -, and _
      const cleanExternalCustomerID = externalCustomerID.replace(/([^a-z0-9_-]+)/gi, '');
      NGX.App.config.externalCustomerID = cleanExternalCustomerID;
      setFormField('ngx_ext_id', cleanExternalCustomerID, null, null, null, true);

      if (!!externalCustomerIDType) {
        const cleanExternalCustomerIDType = externalCustomerIDType.replace(/([^a-z0-9_-]+)/gi, '');
        setFormField('ngx_ext_id_type', cleanExternalCustomerIDType, null, null, null, true);
      }
      return cleanExternalCustomerID;
    },
    setPrivacyConsents = function(consents) {
      const privacyService = NGXD.getServiceContainer().getService('privacy');
      if (!privacyService.isPrivacyManagementEnabled()) {
        console.warn('Warning: The action \'setPrivacyConsents\' is not available because privacy management is not enabled for this campaign.');
        return;
      }
      privacyService.set(consents);
    },

    // send a message to the parent frame's handler
    messageParent = function(msgObj, callback) {
      if (NGX.App.state.framed) {
        var getFrameId = function() {
          if (NGX.context.isPreview) {
            return 'ngxFrame-preview-details';
          } else {
            if (NGX.context.displayContainer)
              return 'ngxFrame' + NGX.context.displayContainer.guid + ':' + NGX.context.displayContainer.id;
          }
        };

        var _theMsg = {
          action:  '',
          event:   '',
          msgId:   Date.now(), // Thank you single threaded JS engine!
          id:      getFrameId(),
          payload: {},
        };

        // TODO(bfriedman) confirm this isn't erroneous from the REBASE
        // NGX.App.state.callbackQueue uses these to trigger callbacks
        if (msgObj.callbackId) {
          _theMsg.callbackId = msgObj.callbackId;
        }

        if ('function' === typeof callback) {
          // Store the callback for later use
          NGX.App.state.callbackQueue[_theMsg.msgId] = callback;
        }

        _.extend(_theMsg, msgObj);

        // Serialise this *always*, this guarantees that we don't introduce behaviour
        // that has to be tested by QA just in an iframe.
        var msgString = '_NGX_' + JSON.stringify(_theMsg);

        log.debug('Messaging Parent with: "' + msgString + '"', 6);
        return window.parent.postMessage(msgString, '*');
      }
    },
    // resize parent iframe
    resizeParent = function(config) {
      if (!NGX.context.isFB && NGX.context.displayContainer && NGX.context.displayContainer.responsive) {
        messageParent(
          {
            action:  config && config.mode ? config.mode : 'resize',
            event:   'frame:resize',
            payload: {
              height: $('body').outerHeight(true),
            },
          },
          function() {
            messageParent({ action: 'getPageInfo' });
          }
        );
      }
    },
    // scroll top frame
    scrollTo = function(x, y) {
      if (NGX.App.state.framed) {
        if (NGX.context.isFB) {
          NGX.Util.requireSDK(log.getDebugLevel() ? 'fbdebug' : 'fb', function() {
            FB.Canvas.scrollTo(x, y);
          });
        } else {
          messageParent({ action: 'scrollTo', payload: { left: x, top: y } });
        }
      } else {
        window.scrollTo(x, y);
      }
    },
    // ensure a passed in element is in the viewport (the top of the element at least
    elementIntoView = function(el) {
      if (!isElInViewport(el[0])) {
        var _top = 0;
        if (State.framed) {
          NGX.App.api.messageParent({ action: 'getpageinfo' }, function(resp) {
            NGX.App.api.scrollTo(0, resp.offsetTop);
          });
        } else {
          NGX.App.api.scrollTo(0, el.position().top);
        }
      }
    },
    isElInViewport = function(el) {
      var rect = el.getBoundingClientRect(),
        vHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.top > vHeight) return false;
      return rect.top > 0;
    },
    // add a hidden field to the campaign form
    /**
     * @param id ID of the field we are going to add
     * @param val Value of this field
     * @param type Content type
     * @param {string} formId ID of the form in which we are adding the field
     * @param {HTMLElement} formElem Reference to the form in which we are going to add the field. Useful in reveal promotions with content pages (WYN-6505)
     * @param {boolean} addToAllMatches Should we add this input to ALL forms which have the "formID" identificator. Useful for fields like customerId
     */
    setFormField = function(id, val, type, formID, formElem, addToAllMatches = false) {

      const addField = (id, val, type, formID, form) => {
        let el;
        let overrideFieldID = null; // This is used for secondary forms, so that we don't end up with two HTML elements with the same ID on a page (breaks the JS)
        // check there is an id and a form
        if (typeof id === 'string' && form) {
          if (formID) {
            switch (formID) {
              // For secondary forms, the inputs have IDs like "email_Secondary"
              case 'xSecondaryForm':
                overrideFieldID = `${id}_Secondary`;
                break;
              // For main forms: just do nothing (nothing is added for input IDs)
              case 'xCampaignForm' :
                break;
              // All other cases, like forms in Reveal Promotions: IDs are like "FORM_ID_email"
              default: overrideFieldID = `${formID}_${id}`;
            }
          }

          // check if field exists
          el = form.querySelector('#' + (overrideFieldID || id));
          if (!el) {
            // otherwise create it
            el = d.createElement('input');
            el.id = overrideFieldID || id;
            el.setAttribute('name', id);
            el.type = typeof type === 'string' ? type : 'hidden';
            form.appendChild(el);
          }
          // set field value
          el.value = val ? val.toString() : '';
        }
        return el;
      };

      if (!addToAllMatches) {
        const form = getForm(formID || 'xCampaignForm', formElem),
          el = addField(id, val, type, formID, form.el);
        return el;
      } else {
        const forms = d.querySelectorAll('#' + (formID || 'xCampaignForm'));
        for (const form of forms) {
          addField(id, val, type, formID, form);
        }
      }
    },
    // return campaign metadata in the format you wanted
    getMetadata = function(prop) {
      var obj = Config.extendedProperties,
        out;
      if (!obj) {
        return false;
      }
      // return the requested item
      if (typeof prop === 'string') {
        return obj[prop];
      }
      // return an array of values as an object containing those
      if (_.isArray(prop)) {
        out = {};
        _.each(prop, function(item) {
          out[item] = obj[item];
        });
        return out;
      }
      // otherwise return the whole object
      return obj;
    },
    getComponents = function() {
      _.each($(conf.component), function(item) {
        if (!item.hasAttribute('data-registered')) {
          log.debug('Adding..... ' + item.id, 2);

          // WYN-743. Backbone discards/ignores models added to a collection with the same id (which in
          // the case of Countdown Reveal promos, happens each time a new promo is opened). Subsequently
          // any listeners registered against the model being added are also not fired (in this case, the listener that
          // initiates view rendering). By first removing the old model (with the same id), it allows the new one
          // to be registered again, and everything works as it should. Disclaimer: *hopefully*.
          Components.remove(item.id);

          // Add the component model.
          Components.add(
            new Component({
              targ: '#' + item.id,
              id:   item.id,
              type: item.getAttribute('data-componenttype'),
            })
          );
          item.setAttribute('data-registered', 'true');
        }
      });
    },
    // main CTA
    appEntry = function(stateName) {
      var form = getForm();
      // Edge case. This is for situation when we have entry closed, and need to navigate to the campaign form.
      // We need this only here, and because of this we don't modify the "getForm" function above.
      if (!form.type) {
        form = { el: document.getElementById('xFormEntryClosed'), type: 'inline' };
      }
      if (form.el) {
        switch (form.type) {
          case 'inline':
            // if a target state is supplied use that, otherwise get the parent state of the inline form.
            NGX.App.api.state(stateName || NGX.Dom.getParentAttr(form.el, 'data-statename'));
            break;
          case 'hidden':
            NGX.entryForm.submitCampaignObjectForm(form.el);
        }
      }
    },
    // WYN-4069 Conditionally load the Facebook SDK if any of the following requirements are met:
    // - standard campaign sharing → facebook sharing enabled
    // - standard campaign sharing → share on confirmation enabled
    // - any module level sharing → facebook sharing enabled
    // - contests → voting → counter+share mode enabled
    // - social auth → facebook auth enabled
    // - claim my content campaigns (use social auth behind the scenes to auth)
    hasFBSDKDependency = function() {
      // Default to no dependency.
      var dependency = false;

      // Alias some deep nested config.
      var appConfig = NGX.App.config;
      var sharingConfig = appConfig.sharingConfig;
      var authConfig = appConfig.socialAuthConfig;

      const falsyValues = [false, 'false'];
      // Campaign level sharing
      if (sharingConfig) {
        if (sharingConfig.facebookShare && !falsyValues.includes(sharingConfig.facebookShare.enabled) || (sharingConfig[sharingNetworks.FACEBOOK] && !falsyValues.includes(sharingConfig[sharingNetworks.FACEBOOK].enabled))) {
          dependency = true;
        }
      }
      // Campaign level confirmation page sharing.
      if (sharingConfig && sharingConfig.facebookConfirmation && !falsyValues.includes(sharingConfig.facebookConfirmation.enabled)) {
        dependency = true;
      }
      // Contest voting strategy.
      if (appConfig.votingStrategy === 'strategy.voting.posttowall') {
        dependency = true;
      }
      // Social auth.
      if (
        authConfig &&
        authConfig.strategy === 'strategy.socialauth.socialsignin' &&
        authConfig.socialSignInNetworks.facebook.enabled === true
      ) {
        dependency = true;
      }
      // Module level sharing.
      if (NGX.App.config.sharing.modulesHaveFBSDKDependency) {
        dependency = true;
      }

      return dependency;
    },
    // Video API.
    videoApi = (function() {
      var mountVideo = function(type, playlist, mountNode) {
        mountVideoPlayer(type, playlist, mountNode);
      };

      return {
        mount: mountVideo,
      };
    })(),
    /**
     * Generic component structure
     */

    // generic component model (components always use this model - use this for standard functionality)
    Component = Backbone.Model.extend({
      defaults:   {},
      initialize: function() {
        // use target element id as the primary key
        this.id = this.get('targ').substring(1);
        // track this component init
        Events.trigger('init:' + this.id, this);
        // add events to queue to process on page ready
        trackQueue.push('view:' + this.attributes.type);
        // debug log the component init
        log.debug('Component ' + this.id + ' initialised', 3);
      },
    }),
    // generic component collection
    ComponentList = Backbone.Collection.extend({
      model: Component,
    }),
    // generic component view
    ComponentView = Backbone.View.extend({
      // we don't trigger a render method on these non-type components because they arrive populated
    }),
    /**
     * Application Structure
     */

    AppModel = Backbone.Model.extend({
      // this is populated elsewhere
    }),
    AppView = Backbone.View.extend({
      el:         conf.app,
      initialize: function() {
        registerViews();
        this.listenTo(Components, 'add', this.addComponent);
        this.model.set('sections', this.$(conf.section));
        NGX.App.api.getComponents();
      },
      // create new component model for each matching element

      // if viewDefs[data-componenttype] use that, otherwise use the ComponentView default
      addComponent: function(model) {
        var _view;
        var obj = {
            model:   model,
            el:      model.has('targ') ? $(model.get('targ')) : false,
            tagName: 'section',
          },
          Fn = viewDef[model.get('type')];
        if (typeof Fn === 'function') {
          _view = new Fn(obj).render();
        } else {
          _view = new ComponentView(obj);
        }

        model.set({ view: _view }, { silent: true });
        return _view;
      },
    }),
    /**
     * Routes
     */

    AppRouter = Backbone.Router.extend({
      initialize: function() {
        //this.createPromoRoutes.call(this);
        this.createStateRoutes.call(this, flowJSON.states);
        stateReady('router');
      },
      createStateRoutes: function(statesObj) {
        // configure any flow routes on this page
        _.each(
          statesObj,
          function(item) {
            var route = item.route;
            if (route) {
              // build route for this state
              this.route(
                route + '(/:id)(?*queryString)',
                route,
                function(id, queryString) {
                  // the optional is parameter is passed in as ID / TODO: do something with it :)
                  // state change triggers the programmatic route, then updates the URL
                  this.stateChange(route);
                },
                this
              );
              // if this item is the default route trigger navigate on landing
              if (!this.defaultState && item.defaultState) {
                this.defaultState = route;
              }
              // add to the availableStates in NGX.context, for reference.
              NGX.context = NGX.context || {};
              NGX.context.availableStates = NGX.context.availableStates || [];
              NGX.context.availableStates.push(route);
            }
          },
          this
        );
        // configure default state route & optionally prevent Facebook's url modification from breaking routing
        if (!this.defaultState && statesObj && statesObj[0]) this.defaultState = statesObj[0].route;

        if ('undefined' !== typeof this.defaultState) {
          // prevent FB from mangling the URL with params being appended after a hash
          this.route(
            '_=_',
            this.defaultState,
            function() {
              // TODO: extract the params and populate our querystring object, rather than just scrubbing them.
              Backbone.history.navigate(this.defaultState + getQuerystring(), { trigger: true, replace: false });
            },
            this
          );

          this.route(
            '(?*queryString)',
            this.defaultState,
            function(queryString) {
              if (queryString) {
                // Found a query string
                // TODO: extract the params and populate our querystring object, rather than just scrubbing them.
                Backbone.history.navigate(this.defaultState + '?' + queryString, { trigger: false, replace: true });
              } else {
                Backbone.history.navigate(this.defaultState, { trigger: false, replace: true });
              }
              this.stateChange(this.defaultState);
            },
            this
          );
        }
      },
      createPromoRoutes: function() {
        NGX.context = NGX.context || {};
        NGX.context.availableStates = NGX.context.availableStates || [];
        NGX.context.availableStates.push('promo');

        this.route('promo(/:id)(?*queryString)', 'promo', function(id, queryString) {
          NGX.App.api.action('display:promo:' + id, {});
        });
      },
      stateChange: function(route) {
        var stateObj = States.get({ route: route });
        if (stateObj) {
          var overlayState = NGX.overlay.get('xO-state');
          if (overlayState) overlayState.destroy();

          Actions.byRef('state:' + stateObj.name, stateObj);
        }
      },
    }),
    /**
     * App Flow Init
     */

    appFlowInit = function() {
      // Start the campaign flow component and create State and Action classes
      var appFlow = NGX.appFlow.init();

      // States and Actions defined here to give the Router access
      States = appFlow.state;
      Actions = appFlow.action;

      // expose API methods - this runs after the App obj is creates & also because of the scoping
      NGX.App.api.register = appFlow.register;
      NGX.App.api.remove = appFlow.remove;
      NGX.App.api.action = Actions.trigger;
      NGX.App.api.state = function(routeName) {
        return Actions.trigger('route:' + routeName);
      };
      NGX.App.api.statePrev = NGX.appFlow.prev;
      NGX.App.api.actionClick = function(e) {
        // Space (32), Enter (13), Android Back Button (4), Esc (27)
        if (e.type === 'keydown' && !([32,13,4,27].includes(e.keyCode))) return;
        var attObj = NGX.Dom.getAttrsByPrefix(this, 'data-ngx-');
        if (attObj.action) {
          e.preventDefault();

          attObj.originalEvent = e;
          if (this.classList.contains('xEchoButton')) {
            attObj.echoInputText = document.getElementById('twitterEchoShareInput').value;
          }
          NGX.App.api.action(attObj.action, attObj);
        }
      };

      // update public & internal app states with data from App Flow
      Events.on('state', function(stateName, stateObj) {
        State.name = stateName;
        App.model.set('state', stateObj);
        NGX.App.api.resizeParent();
      });
    },
    /**
     *  NGX Application start
     */

    AppInit = function() {
      // import default config options and cleanup after ourselves
      mergeAndPurge.call(Config, 'config');
      mergeAndPurge.call(State, 'state');

      // grab the query string & store it
      updateQueryString();

      // add backbone event handler to Events
      _.extend(Events, Backbone.Events);

      // Retrieve parameters from parent's URI
      getParentParams(function(params) {
        if (!params) return;

        // Set referrer value if available
        if (params.hasOwnProperty('r')) {
          setFormField('r', params.r, null, null, null, true);
        }

        // Enable debug mode from parent url
        if (params.hasOwnProperty('debug')) {
          log.setDebugLevel(parseInt(params.debug, 10));
        }
      });

      // build component collection before document ready so the app:ready extends take effect.
      NGX.App.Components = Components = new ComponentList();

      // invite/share on init
      if (Config.shareOnInit) {
        NGX.App.Events.once('state:confirmation', () => {
          switch (Config.shareOnInitMode) {
            case 'share':
              try {
                NGX.App.api.share.confirmation.facebook({ context: 'campaign' });
              } catch (e) {}
              break;
            default:
              // do nothing
              break;
          }
        });
      }

      /** post-load events (document ready, when dom is ready) */
      $(function() {
        // Do this client side, due to server side caching
        applyUAClasses();

        // Apply framed class.
        if (NGX.App.state.framed === true) {
          $('body').addClass('xFramed');
        }

        // get the NGX user ID if it's present
        State.currentUser = State.currentUser || {};

        // Privacy management gating on user cookie consent.
        // If the user does not allow analytics, don't retrieve the tracking UUID from the ngx cookie.
  	    const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
        if (allowCookies) {
          State.currentUser.id = NGX.StorageManager.get('ngx');
        }

        Events.on('all', function(eventName, data) {
          log.debug('[' + eventName + ']', 2);

          var msgObj = {
            action: 'event',
            event:  eventName,
          };
          // If we have a form:entry:success event, we want to pass along the payload.
          // Don't do this for all cases to prevent unforeseen consequences.
          if (eventName === PUB.FORM_ENTRY_SUCCESS && data && data.payload) {
            msgObj = _.extend(msgObj, { payload: data.payload });
          }
          NGX.App.api.messageParent(msgObj);
        });

        // build the main application view (delayed until we have an FB id)
        App = new AppView({
          model: new AppModel(Config.extendedProperties),
        });

        // the app is now initialised
        // IMPORTANT! to catch a component model before it is fully initialised, use the 'init:moduleId' event, rather than appReady
        stateReady('app');

        // Preview pages that aren't in an iframe will need to 'simulate' the 'app:viewport' event for parity.
        if (window.location === top.location) {
          NGX.Analytics.trigger(PUB.APP_VIEWPORT);
        }

        // init auth
        if (Config.hasOwnProperty('socialAuthConfig')) {
          NGX.Auth.init(Config.socialAuthConfig);
        }

        // setup the router
        Router = new AppRouter();

        // start the campaign flow & create API methods
        appFlowInit();

        // start Backbone History - Handles pushState
        Backbone.history = Backbone.history || new Backbone.History({});

        if (
          !Backbone.history.start({
            root:      NGX.context.basePath,
            pushState: typeof history.pushState !== 'undefined',
          })
        ) {
          // 404 state returns undefined from BB.history.start, route to root and allow appFlow to nav to the default route
          Router.navigate('/', { trigger: true });
        }

        // Check for existence of secondary form
        NGX.App.config.hasSecondaryForm = document.getElementById('xSecondaryForm') ? true : false;

        // bind ngx actions
        $(conf.app).on('click', '[data-ngx-action]', NGX.App.api.actionClick);
        // Accessibilty. Ensures button keyboard behavior to non-button elements.
        $(conf.app).on('keydown', '[data-ngx-action][role="button"]', NGX.App.api.actionClick);

        // page has loaded
        stateReady('page');

        // process the insights events after page:ready
        processTrackQueue();

        // Hide campaign behind a modal for unsupported browsers
        // NB: Passing undefined here to allow the default from the method signature to take over
        if (!isSupportedBrowser(undefined, NGX.App.config.usesHelios)) {
          NGX.overlay.create({
            template:       NGX.App.api.getTemplate('browserSupport'),
            type:           'modal',
            disableUIClose: true
          });
        }

        // Check users eligibility to view this campaign
        canSeeCampaign();

        // Attempt to retrieve initial parent window information using the Embed API.
        NGX.App.api.messageParent({ action: 'getpageinfo' }, function(pageInfo) {
          // Cache the page info
          NGX.App.state.pageInfo = pageInfo;

          // If we receive a response, we can safely assume that the parent window Embed API is working.
          NGX.App.state.useApi = true;

          // Set the initial height of our iframe.
          NGX.App.api.resizeParent();

          // Raise an event to signify that the Embed API is ready for use.
          Events.trigger('embed:api:ready');

          // NGX-3977 scroll to top not always happening
          // Temporary workaround for router race condition; can be removed once SPA is default
          if (State.name === 'confirmation' || $('body').hasClass('xConverted')) {
            elementIntoView($('.xPage'));
          }
        });

        Events.on('entry:success:spa', refreshComponents);

      });

      // window load events.
      // This will only fire when the entire document and all resources (including images/background images etc) have finished loading.

      window.addEventListener("load", function() {
        NGX.App.api.resizeParent();
        // WYN-4069 Conditionally load the Facebook SDK if requirements are met.
        if (hasFBSDKDependency()) {
          NGX.Util.requireSDK('fb');
        }
        // WYN-7470. Adding the tracker to observe user's interaction with the page
        NGX.TrackingService.addTrackers(document, NGX.App.config.shouldAddTrackingTokens);
      });
    };

  /**
   * FB Application start
   */

  // post-FB login behaviour
  window.fbAsyncInit = function() {
    if (NGX.context.fb) {
      // facebook API init
      FB.init({
        appId:      NGX.context.fb.app.id,
        channelUrl: NGX.context.displayUrl + '/ui/channelUrl.gsp',
        cookie:     true,
        xfbml:      true,
        version:    NGX.context.fb.apiVersion,
      });

      State.fbLoaded = true;

      if (NGX.context.isFB === true) {
        // scale the canvas if we're in FB
        NGX.Util.resizeCanvas();
        // reset the canvas scroll position
        FB.Canvas.scrollTo(0, 0);
      }
    }
  };

  /**
   * Expose API functions
   */

  return {
    init: AppInit,
    // client API
    api:  {
      /**
       * API methods - methods may be set elsewhere, if the methods are specific to a module look in it's js file.
       */
      // echo params (use to test action refs)
      echo: function() {
        if (console && typeof console.log === 'function') {
          console.log(arguments);
        }
      },
      // register a component view
      registerView:       registerView,
      // refresh component[s] data
      refreshComponents:  refreshComponents,
      // add a string as new handlebars template
      addTemplate:        addTemplate,
      // return a handlebars template function
      getTemplate:        getTemplate,
      // return object containing campaign form
      getComponents:      getComponents,
      // return object containing campaign form
      getForm:            getForm,
      // add hidden field to the campaign form
      setFormField:       setFormField,
      // return campaign metadata (individual value, custom object based on array of keys, or full object)
      getMetadata:        getMetadata,
      // querystring obj as string
      getQuerystring:     getQuerystring,
      // Send message to parent frame
      messageParent:      messageParent,
      // Get the App's test ID for mv + a/b testing
      getTestID:          getTestID,
      // Set the App's test ID for mv + a/b testing
      setTestID:          setTestID,
      // Get the customer's external customer id for entries
      getCustomerID:      getCustomerID,
      // Set the customer's external customer id for entries
      setCustomerID:      setCustomerID,
      // Set the user's choices for cookie / privacy consent
      setPrivacyConsents: setPrivacyConsents,
      // resize the iframe
      resizeParent:       resizeParent,
      // scroll the top frame
      scrollTo:           scrollTo,
      // scroll element into viewport
      elementIntoView:    elementIntoView,
      // apply gate
      gate:               gateApp,
      // share page via FB
      facebookShare:      function(conf) {
        var sharingOpt = $.extend(true, NGX.App.config.sharing, conf);
        if (sharingOpt) {
          facebookShare(sharingOpt);
        }
      },
      // campaign form submission
      campaignEntry: appEntry,

      // Video api.
      video: videoApi,
    },
    // Integrations (Adobe, GTM, GA, etc...)
    integration: {
      Events: IntegrationEvents,
    },
    Events: Events,
    config: Config,
    state:  State,
  };
})(window, document, $, _);
